import { LocalData } from "to-be-removed/LocalData/General/LocalData";
import { ApiDeviationsLoader } from "./loaders/ApiDeviationsLoader";
import { CombinedDeviation } from "./deviations.types";
import { DeviationsChunkLoader } from "./loaders/DeviationsChunkLoader";
import { DatasLoadedListener } from "../DatasLoadedListener";
import { Utilities } from "@utils/utilities";
import { DeviationsLastUpdateLoader } from "./loaders/DeviationsLastUpdateLoader";
import { IRawDeviation } from "@propely/propely-typings";
import { DeviationData } from "to-be-removed/LocalData/Deviation/DeviationData";
import { PropertyData } from "to-be-removed/LocalData/Property/PropertyData";

export class DeviationsLoader {
  private static instance: DeviationsLoader;
  static getInstance(): DeviationsLoader {
    if (!this.instance) {
      this.instance = new DeviationsLoader();
    }
    return this.instance;
  }

  private ids: Set<string> = new Set();
  private datas: CombinedDeviation[] = [];
  private apiLoader: ApiDeviationsLoader;
  private chunkLoader: DeviationsChunkLoader;
  private lastUpdateLoader: DeviationsLastUpdateLoader;
  private subscribers: DatasLoadedListener<CombinedDeviation>[] = [];

  constructor() {
    this.apiLoader = new ApiDeviationsLoader(this);
    this.chunkLoader = new DeviationsChunkLoader(this);
    this.lastUpdateLoader = new DeviationsLastUpdateLoader(this);
  }

  loadAllDeviations = () => {
    return this.apiLoader.loadDeviations();
  };

  loadDeviations = (onUpdate: (datas: CombinedDeviation[]) => void): DatasLoadedListener<CombinedDeviation> => {
    const listener: DatasLoadedListener<CombinedDeviation> = {
      id: Utilities.generateUUID(),
      onUpdate: onUpdate,
      unsubscribe: () => this.removeListener(listener),
    };
    this.subscribers.push(listener);

    if (!this.apiLoader.loaded && !this.apiLoader.loading) {
      this.apiLoader.loadDeviations();
    }
    if (!this.lastUpdateLoader.lastUpdateLoaded()) {
      this.lastUpdateLoader.setupLastUpdateListener();
    }
    if (!this.chunkLoader.loaded && !this.chunkLoader.loading) {
      this.chunkLoader.loadFirstBatch();
    }

    return listener;
  };

  private removeListener = (listener: DatasLoadedListener<CombinedDeviation>) => {
    this.subscribers = this.subscribers.filter((subscriber) => subscriber.id !== listener.id);
    if (this.subscribers.length === 0) {
      this.lastUpdateLoader.removeLastUpdateListener();
    }
  };

  private notifySubscribers = (source: string) => {
    console.log("[DeviationsLoader] Notifying subscribers from " + source);
    for (const subscriber of this.subscribers) {
      subscriber.onUpdate(this.datas);
    }
  };

  /**
   * Adds deviations to this.datas, or updates them if they already exist
   * @param deviations The deviations to add
   */
  addDeviations = (deviations: IRawDeviation[], source: string) => {
    const startTime = new Date().getTime();
    for (const deviation of deviations) {
      if (deviation.deletedBy) {
        this.removeDeviations([deviation], source, false);
        continue;
      }

      const hasDeviation = this.hasDeviation(deviation);
      if (!hasDeviation) {
        const firebaseData = LocalData.properties.addData(deviation.propertyID).deviations.addData(deviation.id);
        firebaseData.setup(deviation);
        firebaseData.endDate = deviation.deadline ? new Date(deviation.deadline) : undefined;
        if (deviation.customTaskType && deviation.customTaskType.parent?.parent) {
          firebaseData.customTaskType = LocalData.companies
            .addData(deviation.customTaskType.parent.parent.id)
            .taskTypes.addData(deviation.customTaskType.id);
        }

        this.datas.push({
          rawData: deviation,
          deviationData: firebaseData,
        });
        this.ids.add(deviation.propertyID + "-" + deviation.id);
      } else {
        const index = this.getIndex(deviation);
        this.datas[index].rawData = deviation;
        this.ids.add(deviation.propertyID + "-" + deviation.id);
        this.datas[index].deviationData.setup(deviation);
        this.datas[index].deviationData.endDate = deviation.deadline ? new Date(deviation.deadline) : undefined;
        if (deviation.customTaskType && deviation.customTaskType.parent?.parent) {
          this.datas[index].deviationData.customTaskType = LocalData.companies
            .addData(deviation.customTaskType.parent.parent.id)
            .taskTypes.addData(deviation.customTaskType.id);
        }
      }
    }

    console.log("[DeviationsLoader] Added", deviations.length, "deviations in", new Date().getTime() - startTime, "ms");

    this.notifySubscribers(source);
  };

  /**
   * @param deviations The deviations to remove
   */
  removeDeviations = (deviations: IRawDeviation[], source: string, notifySubscribers: boolean = true) => {
    for (const deviation of deviations) {
      const index = this.getIndex(deviation);
      if (index !== -1) {
        this.datas.splice(index, 1);
        this.ids.delete(deviation.propertyID + "-" + deviation.id);
      }
    }
    if (notifySubscribers) {
      this.notifySubscribers(source);
    }
  };

  removeDeviationData = (deviationData: DeviationData) => {
    const index = this.datas.findIndex((d) => d.deviationData === deviationData);
    if (index !== -1) {
      this.datas.splice(index, 1);
      this.ids.delete(deviationData.parent?.id + "-" + deviationData.id);
    }
    if (deviationData.parent instanceof PropertyData) {
      deviationData.parent.deviations.removeData(deviationData.id);
    }
    this.notifySubscribers("removeDeviationData");
  };

  /**
   * @param deviation The deviation to find
   * @returns index of deviation in this.datas, or -1 if not found
   */
  getIndex = (deviation: IRawDeviation) => {
    return this.datas.findIndex((d) => d.rawData.id === deviation.id && d.rawData.propertyID === deviation.propertyID);
  };

  hasDeviation = (deviation: IRawDeviation) => {
    return this.ids.has(deviation.propertyID + "-" + deviation.id);
  };

  getDeviation = (propertyID: string | undefined, deviationID: string | undefined) => {
    return this.datas.find((d) => d.rawData.id === deviationID && d.rawData.propertyID === propertyID);
  };

  getAllDeviationDatas = () => {
    return this.datas.map((d) => d.deviationData);
  };

  areAllLoaded = () => {
    return this.apiLoader.loaded;
  };
}

